import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import DomainStatusBadge from './DomainStatusBadge';
import DNSInstructionsPanel from './DNSInstructionsPanel';
import Header from '../layout/header';
import Footer from '../layout/footer';
import fetchWithJWT from '../api/fetchWithJWT';
import { usePlan } from '../context/PlanContext';
import UpgradePrompt from './UpgradePrompt';

const DomainSettings = () => {
  const navigate = useNavigate();
  const { canAddDomain, planInfo, isReadOnly } = usePlan();
  const [loading, setLoading] = useState(true);
  const [domainInfo, setDomainInfo] = useState(null);
  const [customDomain, setCustomDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [confirmDomainText, setConfirmDomainText] = useState('');
  const [verificationErrors, setVerificationErrors] = useState([]);
  const [domainCount, setDomainCount] = useState(0);
  
  // OTP states
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [isRequestingOtp, setIsRequestingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [pendingDomain, setPendingDomain] = useState('');
  const [otpPhoneNumber, setOtpPhoneNumber] = useState('');

  const businessId = localStorage.getItem('business_id');
  const apiUrl = process.env.REACT_APP_APIURL;

  useEffect(() => {
    fetchDomainInfo();
  }, []);

  const fetchDomainInfo = async () => {
    try {
      setLoading(true);
      const response = await fetchWithJWT(`/business/domain/info?user_id=${businessId}`);
      const data = await response.json();

      if (data.status === 'success') {
        setDomainInfo(data.result);
        // Count domains (1 if custom_domain exists, 0 otherwise)
        setDomainCount(data.result.custom_domain ? 1 : 0);
      } else {
        // No domain configured
        setDomainInfo({ domain_status: 'none' });
        setDomainCount(0);
      }
    } catch (error) {
      console.error('Error fetching domain info:', error);
      toast.error('Failed to load domain information');
      setDomainInfo({ domain_status: 'none' });
      setDomainCount(0);
    } finally {
      setLoading(false);
    }
  };

  const validateDomainFormat = (domain) => {
    // Remove protocol if present
    let cleanDomain = domain.trim().toLowerCase();
    cleanDomain = cleanDomain.replace(/^https?:\/\//, '');
    cleanDomain = cleanDomain.replace(/\/$/, ''); // Remove trailing slash
    
    // Check for path or query parameters
    if (cleanDomain.includes('/') || cleanDomain.includes('?')) {
      return { valid: false, message: 'Domain should not include path or query parameters' };
    }

    // Basic domain format validation
    const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
    if (!domainRegex.test(cleanDomain)) {
      return { valid: false, message: 'Invalid domain format. Example: www.yourdomain.com' };
    }

    return { valid: true, cleanDomain };
  };

  const handleSubmitDomain = async (e) => {
    e.preventDefault();
    
    // Check if user is in read-only mode
    if (isReadOnly) {
      toast.error('Your plan has expired. Please upgrade to add a custom domain.');
      toast.info('Click here to upgrade', {
        autoClose: 5000,
        onClick: () => navigate('/purchase-plan')
      });
      return;
    }

    // Check domain limit
    const limitCheck = canAddDomain(domainCount);
    if (!limitCheck.allowed) {
      toast.error(limitCheck.reason);
      toast.info('Click here to upgrade your plan', {
        autoClose: 5000,
        onClick: () => navigate('/purchase-plan')
      });
      return;
    }
    
    const validation = validateDomainFormat(customDomain);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    // Store the validated domain for later use
    setPendingDomain(validation.cleanDomain);
    
    // Request OTP first
    await requestOtp();
  };

  const requestOtp = async () => {
    setIsRequestingOtp(true);
    try {
      const response = await fetchWithJWT('/business/domain/request-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: businessId
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success(data.message || 'OTP sent successfully to your registered mobile number.');
        setOtpPhoneNumber(data.result?.phone_number || '');
        setShowOtpModal(true);
        setOtp('');
      } else {
        toast.error(data.error?.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error requesting OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsRequestingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const response = await fetchWithJWT('/business/domain/verify-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: businessId,
          otp: otp
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success(data.message || 'OTP verified successfully!');
        setShowOtpModal(false);
        setOtp('');
        
        // Now submit the domain
        await submitDomainAfterOtp();
      } else {
        toast.error(data.error?.message || 'Invalid OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('Failed to verify OTP. Please try again.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const submitDomainAfterOtp = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetchWithJWT('/business/domain/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: businessId,
          custom_domain: pendingDomain
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Domain submitted successfully! Our admin team will review it shortly.');
        setCustomDomain('');
        setPendingDomain('');
        fetchDomainInfo(); // Refresh domain info
      } else {
        toast.error(data.error?.message || 'Failed to submit domain');
      }
    } catch (error) {
      console.error('Error submitting domain:', error);
      toast.error('Failed to submit domain. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyDNS = async () => {
    setIsVerifying(true);
    setVerificationErrors([]);
    
    try {
      const response = await fetchWithJWT('/business/domain/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: businessId
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Domain verified successfully! Your custom domain is now active.');
        fetchDomainInfo(); // Refresh to show active status
      } else {
        if (data.error?.details?.errors) {
          setVerificationErrors(data.error.details.errors);
          toast.error('DNS verification failed. Please check the errors below.');
        } else {
          toast.error(data.error?.message || 'DNS verification failed');
        }
      }
    } catch (error) {
      console.error('Error verifying DNS:', error);
      toast.error('Failed to verify DNS. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleUpdateDomain = async () => {
    const validation = validateDomainFormat(newDomain);
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetchWithJWT('/business/domain/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: businessId,
          new_domain: validation.cleanDomain
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Domain updated successfully! Your old domain will remain active until the new one is verified.');
        setShowUpdateModal(false);
        setNewDomain('');
        fetchDomainInfo();
      } else {
        toast.error(data.error?.message || 'Failed to update domain');
      }
    } catch (error) {
      console.error('Error updating domain:', error);
      toast.error('Failed to update domain. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (confirmDomainText !== domainInfo.custom_domain) {
      toast.error('Domain name does not match. Please type the exact domain name to confirm.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetchWithJWT('/business/domain/remove', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: businessId
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Domain removed successfully');
        setShowRemoveModal(false);
        setConfirmDomainText('');
        fetchDomainInfo();
      } else {
        toast.error(data.error?.message || 'Failed to remove domain');
      }
    } catch (error) {
      console.error('Error removing domain:', error);
      toast.error('Failed to remove domain. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="wrapper">
        <Header />
        <div className="content-wrapper">
          <section className="content">
            <div className="container-fluid">
              <div className="text-center mt-5">
                <i className="fa-solid fa-spinner fa-spin fa-3x"></i>
                <p className="mt-3">Loading domain settings...</p>
              </div>
            </div>
          </section>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="wrapper">
      <Header />
      <div className="content-wrapper">
        <section className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1>Custom Domain Settings</h1>
              </div>
            </div>
          </div>
        </section>

        <section className="content">
          <div className="container-fluid">
            {/* Current Domain Status */}
            {domainInfo && domainInfo.domain_status !== 'none' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fa-solid fa-globe mr-2"></i>
                    Current Domain Configuration
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <p><strong>Domain:</strong></p>
                      <p className="text-lg">{domainInfo.custom_domain}</p>
                    </div>
                    <div className="col-md-6">
                      <p><strong>Status:</strong></p>
                      <DomainStatusBadge status={domainInfo.domain_status} />
                    </div>
                  </div>

                  {domainInfo.domain_requested_at && (
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <p className="text-muted">
                          <small>Requested: {new Date(domainInfo.domain_requested_at).toLocaleString()}</small>
                        </p>
                      </div>
                      {domainInfo.domain_activated_at && (
                        <div className="col-md-6">
                          <p className="text-muted">
                            <small>Activated: {new Date(domainInfo.domain_activated_at).toLocaleString()}</small>
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {domainInfo.domain_failure_reason && (
                    <div className="alert alert-danger mt-3">
                      <strong>Error:</strong> {domainInfo.domain_failure_reason}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-4">
                    <button
                      className="btn btn-warning mr-2"
                      onClick={() => setShowUpdateModal(true)}
                    >
                      <i className="fa-solid fa-edit mr-1"></i>
                      Change Domain
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={() => setShowRemoveModal(true)}
                    >
                      <i className="fa-solid fa-trash mr-1"></i>
                      Remove Domain
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add Domain Form (only if no domain) */}
            {domainInfo && domainInfo.domain_status === 'none' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fa-solid fa-plus-circle mr-2"></i>
                    Add Custom Domain
                  </h3>
                </div>
                <div className="card-body">
                  <p>
                    Configure a custom domain for your store to provide a professional branded experience for your customers.
                  </p>
                  <form onSubmit={handleSubmitDomain}>
                    <div className="form-group">
                      <label htmlFor="customDomain">Custom Domain</label>
                      <input
                        type="text"
                        className="form-control"
                        id="customDomain"
                        placeholder="www.yourdomain.com"
                        value={customDomain}
                        onChange={(e) => setCustomDomain(e.target.value)}
                        required
                      />
                      <small className="form-text text-muted">
                        Enter your domain without http:// or https://. Example: www.yourdomain.com
                      </small>
                    </div>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting || isRequestingOtp}
                    >
                      {isRequestingOtp ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                          Sending OTP...
                        </>
                      ) : isSubmitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="fa-solid fa-paper-plane mr-1"></i>
                          Submit Domain
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Status-specific content */}
            {domainInfo && domainInfo.domain_status === 'pending' && (
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-info">
                    <h5>
                      <i className="fa-solid fa-clock mr-2"></i>
                      Pending Admin Review
                    </h5>
                    <p className="mb-0">
                      Your domain request has been submitted and is awaiting review by our admin team. 
                      You will receive an email notification once the DNS configuration is ready.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* DNS Instructions Panel */}
            {domainInfo && (domainInfo.domain_status === 'dns_updated' || domainInfo.domain_status === 'failed') && (
              <>
                <DNSInstructionsPanel
                  domain_dns_records={domainInfo.domain_dns_records}
                  domain_status={domainInfo.domain_status}
                  onVerify={handleVerifyDNS}
                  isVerifying={isVerifying}
                />

                {/* Verification Errors */}
                {verificationErrors.length > 0 && (
                  <div className="card">
                    <div className="card-header bg-danger">
                      <h3 className="card-title">
                        <i className="fa-solid fa-exclamation-triangle mr-2"></i>
                        DNS Verification Errors
                      </h3>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled">
                        {verificationErrors.map((error, index) => (
                          <li key={index} className="mb-2">
                            <i className="fa-solid fa-times-circle text-danger mr-2"></i>
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Active Domain Info */}
            {domainInfo && domainInfo.domain_status === 'active' && (
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-success">
                    <h5>
                      <i className="fa-solid fa-check-circle mr-2"></i>
                      Domain Active
                    </h5>
                    <p className="mb-0">
                      Your custom domain is active and serving your store. Customers can now access your store at{' '}
                      <a href={`https://${domainInfo.custom_domain}`} target="_blank" rel="noopener noreferrer">
                        {domainInfo.custom_domain}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />

      {/* Update Domain Modal */}
      {showUpdateModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Domain</h5>
                <button type="button" className="close" onClick={() => setShowUpdateModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="fa-solid fa-info-circle mr-2"></i>
                  Your old domain will remain active until the new domain is verified.
                </div>
                <div className="form-group">
                  <label htmlFor="newDomain">New Domain</label>
                  <input
                    type="text"
                    className="form-control"
                    id="newDomain"
                    placeholder="www.yournewdomain.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowUpdateModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateDomain}
                  disabled={isSubmitting || !newDomain}
                >
                  {isSubmitting ? 'Updating...' : 'Update Domain'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Domain Modal */}
      {showRemoveModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger">
                <h5 className="modal-title text-white">Remove Domain</h5>
                <button type="button" className="close text-white" onClick={() => setShowRemoveModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-danger">
                  <i className="fa-solid fa-exclamation-triangle mr-2"></i>
                  <strong>Warning:</strong> This action will remove your custom domain. 
                  Your store will only be accessible via the default subdomain.
                </div>
                <p>To confirm, please type your domain name: <strong>{domainInfo.custom_domain}</strong></p>
                <div className="form-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type domain name to confirm"
                    value={confirmDomainText}
                    onChange={(e) => setConfirmDomainText(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRemoveModal(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleRemoveDomain}
                  disabled={isSubmitting || confirmDomainText !== domainInfo.custom_domain}
                >
                  {isSubmitting ? 'Removing...' : 'Remove Domain'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-primary">
                <h5 className="modal-title text-white">
                  <i className="fa-solid fa-shield-halved mr-2"></i>
                  Verify OTP
                </h5>
                <button 
                  type="button" 
                  className="close text-white" 
                  onClick={() => {
                    setShowOtpModal(false);
                    setOtp('');
                    setPendingDomain('');
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <i className="fa-solid fa-info-circle mr-2"></i>
                  A 4-digit OTP has been sent to your registered mobile number
                  {otpPhoneNumber && ` ending with ${otpPhoneNumber.slice(-4)}`}.
                </div>
                <p className="mb-3">Please enter the OTP to verify and submit your domain.</p>
                <div className="form-group">
                  <label htmlFor="otpInput">Enter OTP</label>
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    id="otpInput"
                    placeholder="Enter 4-digit OTP"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 4);
                      setOtp(value);
                    }}
                    maxLength="4"
                    autoFocus
                    style={{ letterSpacing: '0.5em', fontSize: '1.5rem' }}
                  />
                  <small className="form-text text-muted">
                    OTP is valid for 5 minutes
                  </small>
                </div>
                <div className="text-center mt-3">
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={requestOtp}
                    disabled={isRequestingOtp}
                  >
                    {isRequestingOtp ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                        Resending...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-redo mr-1"></i>
                        Resend OTP
                      </>
                    )}
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowOtpModal(false);
                    setOtp('');
                    setPendingDomain('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleVerifyOtp}
                  disabled={isVerifyingOtp || otp.length !== 4}
                >
                  {isVerifyingOtp ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check mr-1"></i>
                      Verify & Submit
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DomainSettings;
